import { ArrowRight, BookOpenText, GraduationCap, IdCard } from 'lucide-react';
import { School } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import ReactSelect, { Props as SelectProps } from 'react-select';

import Field from '~components/ui/Field';
import Form from '~components/ui/Form';
import Input from '~components/ui/Input';
import Loading from '~components/ui/Loading';
import Notification from '~components/ui/Notification';
import { getFromAPI } from '~utils/api/common';

export interface StudentInfo {
  school: string;
  major: string;
  accountNumber: string;
}

interface FormValues extends Omit<StudentInfo, 'school' | 'major'> {
  school: { label: string; value: string };
  major: { label: string; value: string };
}

interface Props {
  schools: { school: string; majors: string[] }[];
  onContinue: (data: StudentInfo) => void;
}

const selectClassnames: SelectProps['classNames'] = {
  control: (state) =>
    state.isFocused
      ? '!ring-1 !ring !ring-neutral-600 !border !border-neutral-500 dark:!bg-accent'
      : 'shadow border !border-input dark:!border-neutral-500 dark:!bg-neutral-800',
  singleValue: () => 'dark:!text-white !text-sm',
  option: (state) => {
    if (state.isFocused) return '!bg-primary/5 !text-primary dark!:bg-primary/20';
    if (state.isSelected) return '!bg-primary !text-white';
    return '';
  },
  placeholder: () => 'dark:!text-neutral-300',
  indicatorSeparator: () => 'dark:!bg-neutral-500',
  container: () => 'max-w-full',
  menu: () => 'dark:!bg-accent dark:!text-white',
};

const StudentInfoForm: FC<Props> = ({ schools, onContinue }) => {
  const [loading, setLoading] = useState(false);
  const form = Form.useForm<FormValues>();
  const school = form.watch('school');
  const majors = useMemo(
    () => (school ? schools.find((item) => item.school === school.value)?.majors : []),
    [school, schools],
  );
  const handleSubmit = (data: FormValues) => {
    setLoading(true);
    const searchParams = new URLSearchParams();
    searchParams.append('school', data.school.value);
    searchParams.append('major', data.major.value);
    searchParams.append('accountNumber', data.accountNumber);
    getFromAPI(`/uas-students/search?${searchParams.toString()}`, { notifyErrors: false })
      .then(() => {
        onContinue({
          school: data.school.value,
          major: data.major.value,
          accountNumber: data.accountNumber,
        });
      })
      .catch((e) => {
        Notification.error({
          title: e.message,
          message: 'Verifica tu información',
          placement: 'top',
        });
      })
      .finally(() => setLoading(false));
  };
  return (
    <Loading show={loading} size="normal" label="Validando información">
      <div className="flex flex-col items-center gap-6 py-8 px-6 md:px-8 max-w-2xl">
        <span className="rounded-full bg-primary/5 p-4 text-primary dark:bg-primary/20">
          <GraduationCap size={32} />
        </span>
        <div>
          <h1 className="h3 font-bold text-center">Información Estudiantil</h1>
          <p className="text-support">
            Ingresa tus datos académicos para verificar tu identidad como estudiante UAS
          </p>
        </div>
        <Form form={form} onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
          <Field
            name="school"
            label={
              <span className="flex items-center gap-2">
                <School />
                <span>Escuela o Facultad</span>
              </span>
            }
            rules={[{ required: true, message: 'Selecciona tu escuela a facultad' }]}
            forceController
            className="max-w-full"
          >
            <ReactSelect
              options={schools.map((item) => ({ label: item.school, value: item.school }))}
              placeholder="Selecciona tu escuela"
              classNames={selectClassnames}
            />
          </Field>
          <Field
            name="major"
            label={
              <span className="flex items-center gap-2">
                <BookOpenText />
                <span>Carrera</span>
              </span>
            }
            rules={[{ required: true, message: 'Selecciona tu carrera' }]}
            forceController
          >
            <ReactSelect
              options={majors?.map((item) => ({ label: item, value: item }))}
              placeholder={!school ? 'Primero selecciona tu escuela.' : 'Selecciona tu carrera'}
              classNames={selectClassnames}
            />
          </Field>
          <Field
            name="accountNumber"
            label={
              <span className="flex items-center gap-2">
                <IdCard />
                <span>Matrícula</span>
              </span>
            }
            help="Ingreresa tu número de matrícula tal como aparece en tu credencial estudiantil."
            rules={{ required: true, message: 'Ingresa tu matrícula' }}
          >
            <Input placeholder="EJ: 202400001" />
          </Field>
          <button className="btn btn-primary w-full" type="submit">
            <span>Continuar</span> <ArrowRight size={16} />
          </button>
        </Form>
      </div>
    </Loading>
  );
};

export default StudentInfoForm;
